/**
 * Utilitaires pour la gestion des dates et fuseaux horaires
 * 
 * Ce fichier contient des fonctions helper pour :
 * - Formater des dates en tenant compte du fuseau horaire local
 * - Convertir des dates locales en UTC pour les requêtes serveur
 * - Parser des dates sans problème de fuseau horaire
 */

/**
 * Formate une date en YYYY-MM-DD sans conversion UTC
 * Utilise les méthodes locales (getFullYear, getMonth, getDate) qui retournent
 * les valeurs dans le fuseau horaire local du navigateur
 * 
 * @param date - La date à formater
 * @returns La date au format "YYYY-MM-DD" (ex: "2026-01-21")
 * 
 * @example
 * const date = new Date("2026-01-21T08:00:00+01:00");
 * formatDateLocal(date); // "2026-01-21"
 */
export function formatDateLocal(date: Date): string {
  // getFullYear() retourne l'année dans le fuseau horaire local
  const year = date.getFullYear();
  
  // getMonth() retourne le mois (0-11) dans le fuseau horaire local
  // On ajoute 1 car les mois sont 0-indexés (0 = janvier, 11 = décembre)
  const month = String(date.getMonth() + 1).padStart(2, "0");
  
  // getDate() retourne le jour du mois (1-31) dans le fuseau horaire local
  const day = String(date.getDate()).padStart(2, "0");
  
  // padStart(2, "0") ajoute un zéro devant si le nombre est < 10
  // Exemple: "1" devient "01", "12" reste "12"
  return `${year}-${month}-${day}`;
}

/**
 * Parse une date au format YYYY-MM-DD en date locale (pas UTC)
 * 
 * parseISO de date-fns interprète "YYYY-MM-DD" comme UTC, ce qui cause
 * un décalage d'un jour selon le fuseau horaire du navigateur.
 * Cette fonction crée une date locale à minuit sans conversion UTC.
 * 
 * @param dateString - La date au format "YYYY-MM-DD" (ex: "2026-01-21")
 * @returns Un objet Date représentant cette date à minuit dans le fuseau horaire local
 * 
 * @example
 * parseLocalDate("2026-01-21"); 
 * // Crée une date le 21/01/2026 à 00h00 dans le fuseau horaire local
 * // (pas de conversion UTC, donc pas de décalage)
 */
export function parseLocalDate(dateString: string): Date {
  // Séparer la chaîne "YYYY-MM-DD" en composants
  // Exemple: "2026-01-21" → ["2026", "01", "21"]
  const [year, month, day] = dateString.split("-").map(Number);
  
  // Créer une date locale à minuit
  // new Date(year, month - 1, day) crée une date dans le fuseau horaire local
  // month - 1 car les mois sont 0-indexés (0 = janvier, 11 = décembre)
  // Exemple: new Date(2026, 0, 21) = 21 janvier 2026 à 00h00 locale
  return new Date(year, month - 1, day);
}

/**
 * Convertit le début de journée local en UTC pour les requêtes serveur
 * 
 * Les dates stockées en base de données sont en UTC. Quand on filtre par date,
 * on doit convertir correctement le début de journée local en UTC.
 * 
 * Exemple en Europe/Paris (UTC+1) :
 * - Début du 21/01 à 00h00 local = 20/01 à 23h00 UTC
 * - Cette fonction retourne "2026-01-20T23:00:00.000Z"
 * 
 * @param date - La date pour laquelle on veut le début de journée en UTC
 * @returns La date au format ISO string UTC (ex: "2026-01-20T23:00:00.000Z")
 * 
 * @example
 * const date = new Date("2026-01-21");
 * getStartOfDayUTC(date); 
 * // En UTC+1: "2026-01-20T23:00:00.000Z"
 * // En UTC+0: "2026-01-21T00:00:00.000Z"
 */
export function getStartOfDayUTC(date: Date): string {
  // Créer une copie de la date pour ne pas modifier l'originale
  const localStart = new Date(date);
  
  // Définir l'heure à minuit (00:00:00.000) dans le fuseau horaire local
  // setHours() modifie l'heure dans le fuseau horaire local du navigateur
  localStart.setHours(0, 0, 0, 0);
  
  // Convertir en UTC avec toISOString()
  // Si on est en UTC+1, minuit local devient 23h00 la veille en UTC
  // Exemple: 21/01 00h00 UTC+1 → 20/01 23h00 UTC
  return localStart.toISOString();
}

/**
 * Convertit la fin de journée local en UTC pour les requêtes serveur
 * 
 * Les dates stockées en base de données sont en UTC. Quand on filtre par date,
 * on doit convertir correctement la fin de journée local en UTC.
 * 
 * Exemple en Europe/Paris (UTC+1) :
 * - Fin du 21/01 à 23h59 local = 21/01 à 22h59 UTC
 * - Cette fonction retourne "2026-01-21T22:59:59.999Z"
 * 
 * @param date - La date pour laquelle on veut la fin de journée en UTC
 * @returns La date au format ISO string UTC (ex: "2026-01-21T22:59:59.999Z")
 * 
 * @example
 * const date = new Date("2026-01-21");
 * getEndOfDayUTC(date); 
 * // En UTC+1: "2026-01-21T22:59:59.999Z"
 * // En UTC+0: "2026-01-21T23:59:59.999Z"
 */
export function getEndOfDayUTC(date: Date): string {
  // Créer une copie de la date pour ne pas modifier l'originale
  const localEnd = new Date(date);
  
  // Définir l'heure à 23h59:59.999 dans le fuseau horaire local
  // setHours(23, 59, 59, 999) = 23h59:59.999
  localEnd.setHours(23, 59, 59, 999);
  
  // Convertir en UTC avec toISOString()
  // Si on est en UTC+1, 23h59 local devient 22h59 en UTC
  // Exemple: 21/01 23h59 UTC+1 → 21/01 22h59 UTC
  return localEnd.toISOString();
}

/**
 * Formate une durée en heures au format lisible "Xh Ym"
 * 
 * @param hours - Durée en heures décimales (ex: 4.5 pour 4h30)
 * @returns Durée formatée (ex: "4h 30m") ou "-" si la durée est 0 ou nulle
 * 
 * @example
 * formatDuration(4.5);   // "4h 30m"
 * formatDuration(0.5);   // "30m"
 * formatDuration(8.25);  // "8h 15m"
 * formatDuration(0);     // "-"
 */
export function formatDuration(hours: number): string {
  // Si la durée est nulle, 0 ou non définie, retourner "-"
  if (!hours || hours === 0) return "-";

  // Convertir les heures décimales en minutes totales
  // Exemple: 4.5 heures = 4.5 * 60 = 270 minutes
  const totalMinutes = Math.round(hours * 60);

  // Calculer les heures et minutes
  // Math.floor() arrondit vers le bas
  // Exemple: 270 minutes = Math.floor(270/60) = 4 heures
  const h = Math.floor(totalMinutes / 60);
  
  // Le reste de la division donne les minutes
  // Exemple: 270 % 60 = 30 minutes
  const m = totalMinutes % 60;

  // Si on a des heures, afficher "Xh Ym"
  // Sinon, afficher seulement "Ym"
  if (h > 0) {
    return `${h}h ${m}m`;
  } else {
    return `${m}m`;
  }
}
